import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as api from '../api';
import LoginLayout from '../components/LoginLayout';
import PwResetForm from '../components/PwResetForm';
import pwResetImage from '../assets/icons/pw-reset.svg';
import NewPasswordForm from '../components/NewPasswordForm';
import SvgIcon from '../components/SvgIcon';
import ValidationForm from '../components/ValidationForm';
import { passwordMatchCheck, hiddenPhoneChars } from '../utils';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';

export default function PwReset() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [timeLeft, setTimeLeft] = useState(179);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const [screen, setScreen] = useState<'reset' | 'validation' | 'new pass'>(
    qs.get('key') ? 'new pass' : 'reset',
  );
  const [passwordResetKey, setPasswordResetKey] = useState<string>(
    qs.get('key') || '',
  );
  const showSnackbar = useAlertSnackbar();

  const handleSmsVerification = async (otpCode: any) => {
    try {
      const res = await api.smsVerification(phoneNumber, otpCode);
      if (res.status === 200) {
        const key = await res.text();
        setPasswordResetKey(key);
        nav(`/reset?key=${key}`);
        setScreen('new pass');
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      showSnackbar(
        e.detail || t('pwResetPage.smsVerificationErrorMessage'),
        'error',
      );
    }
  };

  const handleSendSmsForReset = async () => {
    try {
      const res = await api.sendSmsForReset(
        phoneNumber,
        'MEMBER_PASSWORD_RESET',
        'SMS',
      );
      if (res.status === 200) {
        setTimeLeft(179);
        setScreen('validation');
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      showSnackbar(
        e.detail || t('pwResetPage.smsNotSendErrorMessage'),
        'error',
      );
    }
  };
  const handleSendWpForReset = async () => {
    try {
      const res = await api.sendSmsForReset(
        phoneNumber,
        'MEMBER_PASSWORD_RESET',
        'WA_TEMPLATE',
      );
      if (res.status === 200) {
        setTimeLeft(179);
        setScreen('validation');
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      showSnackbar(
        e.detail || t('pwResetPage.smsNotSendErrorMessage'),
        'error',
      );
    }
  };

  const handlePasswordReset = async (
    newPassword: string,
    newPasswordAgain: string,
  ) => {
    try {
      if (!passwordMatchCheck(newPassword, newPasswordAgain)) {
        showSnackbar(t('pwResetPage.passwordMatchErrorMessage'), 'error');
        return;
      }

      const res = await api.passwordReset(passwordResetKey, newPassword);
      if (res.status === 200) {
        showSnackbar(t('pwResetPage.pwResetSuccesMessage'), 'success');
        setTimeout(() => {
          nav('/login');
        }, 3000);
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      showSnackbar(
        e.detail || t('pwResetPage.passwordMatchErrorMessage'),
        'error',
      );
    }
  };

  return (
    <LoginLayout>
      {screen !== 'new pass' && (
        <>
          <Typography
            variant="h6"
            sx={{ textAlign: 'left', mb: 1, fontWeight: 900 }}
          >
            {screen === 'reset'
              ? t('pwResetPage.pwResetTitle')
              : t('pwResetPage.validationTitle')}
          </Typography>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              textAlign: 'left',
              alignItems: 'start',
            }}
          >
            <SvgIcon
              src={pwResetImage}
              style={{ fill: 'currentColor', width: 36, marginTop: 4 }}
            />
            <Typography
              sx={{ fontSize: 14, ml: 2, mr: 4 }}
              dangerouslySetInnerHTML={{
                __html:
                  screen === 'reset'
                    ? t(`pwResetPage.pwResetInfo`)
                    : t(`pwResetPage.validationInfo`, {
                        phoneNumber: hiddenPhoneChars(phoneNumber),
                      }),
              }}
            />
          </Box>
        </>
      )}

      <Box sx={{ maxWidth: '100%', mt: 4 }}>
        {screen === 'reset' ? (
          <PwResetForm
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            onSmsSubmit={handleSendSmsForReset}
            onWpSubmit={handleSendWpForReset}
          />
        ) : screen === 'validation' ? (
          <ValidationForm
            onSubmit={handleSmsVerification}
            retrySendSms={handleSendSmsForReset}
            timeLeft={timeLeft}
            setTimeLeft={setTimeLeft}
          />
        ) : (
          <NewPasswordForm onSubmit={handlePasswordReset} />
        )}
      </Box>
    </LoginLayout>
  );
}
